/**
 * TourProvider — Global cross-page tour state management.
 *
 * Renders react-joyride in controlled mode.
 * Handles cross-route navigation, step skipping, and persistence.
 */
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Joyride, ACTIONS, EVENTS, STATUS } from 'react-joyride';
import TourTooltip from './TourTooltip';
import { TOUR_STORAGE_KEY, checkBackendTourFlag } from './tourConfig';
import dashboardSteps from './steps/dashboardSteps';
import uploadSteps from './steps/uploadSteps';
import timelineSteps from './steps/timelineSteps';
import insightsSteps from './steps/insightsSteps';
import './tour.css';

console.log("[TourProvider Mount] The imported Joyride is:", typeof Joyride, Joyride);

/** Merge all steps into one ordered array for the full onboarding flow */
const ALL_STEPS = [
  ...dashboardSteps,
  ...uploadSteps,
  ...timelineSteps,
  ...insightsSteps,
];

import { TourContext } from './useTour';

/**
 * Optionally filter steps based on user context.
 * If user already has reports, we skip the upload-focused steps
 * to prioritize insights.
 */
function buildSteps(hasReports) {
  // Always include all steps so users get the full guided experience.
  return [...ALL_STEPS];
}

/**
 * Wait for a target element to exist in the DOM.
 * Returns true if found within timeout, false otherwise.
 */
function waitForTarget(selector, timeoutMs = 2000) {
  return new Promise((resolve) => {
    if (document.querySelector(selector)) {
      resolve(true);
      return;
    }
    const start = Date.now();
    const interval = setInterval(() => {
      if (document.querySelector(selector)) {
        clearInterval(interval);
        resolve(true);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(interval);
        resolve(false);
      }
    }, 100);
  });
}

export default function TourProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Tour state
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [steps, setSteps] = useState(() => buildSteps(false));
  const [isTourActive, setIsTourActive] = useState(false);

  // Flags
  const [hasCheckedFlag, setHasCheckedFlag] = useState(false);

  // Check if tour was already seen (on mount)
  useEffect(() => {
    let cancelled = false;
    checkBackendTourFlag().then((seen) => {
      if (!cancelled) setHasCheckedFlag(true);
      // Auto-start only happens via Dashboard — not here
    });
    return () => { cancelled = true; };
  }, []);


  /** Mark tour as completed */
  const completeTour = useCallback(() => {
    setRun(false);
    setStepIndex(0);
    setTimeout(() => {
      setIsTourActive(false);
      document.body.style.pointerEvents = 'auto';
      document.body.style.overflow = '';
      document.body.classList.remove('react-joyride__body-noscroll');
    }, 150);
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
  }, []);

  /**
   * Start (or restart) the onboarding tour.
   * @param {{ hasReports?: boolean }} options
   */
  const startTour = useCallback((options = {}) => {
    console.log("[Tour] startTour invoked with options:", options);
    const newSteps = buildSteps(options.hasReports || false);
    setSteps(newSteps);
    setStepIndex(0);
    setIsTourActive(true);
    setRun(false);
    
    // Give react-joyride a dedicated tick to mount and clear its native state machine
    // This resolves the React 18 Strict Mode fatal loop.
    setTimeout(() => {
      setRun(true);
    }, 150);
  }, []);

  /** Stop (pause) the tour without marking as complete */
  const stopTour = useCallback(() => {
    setRun(false);
    setTimeout(() => {
      setIsTourActive(false);
      document.body.style.pointerEvents = 'auto';
      document.body.style.overflow = '';
      document.body.classList.remove('react-joyride__body-noscroll');
    }, 150);
  }, []);

  /** Reset tour flag so it can be started fresh */
  const resetTour = useCallback(() => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    setStepIndex(0);
    setRun(false);
    setIsTourActive(false);
  }, []);

  /** Joyride controlled callback — the heart of cross-page navigation */
  const handleJoyrideCallback = useCallback(
    (data) => {
      const { action, index, status, type } = data;
      console.log(`[Tour] Joyride Event: ${type}`, data);

      if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
        console.log(`[Tour] Tour completed or skipped! Status: ${status}`);
        // Mark as finished locally
        setRun(false);
        setStepIndex(0);
        
        // Failsafe: Remove any stuck Joyride overlay or body styles manually
        document.body.style.pointerEvents = 'auto'; // Explicitly restore clicks
        document.body.style.overflow = '';
        document.body.classList.remove('react-joyride__body-noscroll'); // Remove Joyride's class
        
        // Remove left-over portals forcefully
        const overlay = document.querySelector('.__floater__overlay');
        const portal = document.getElementById('react-joyride-portal');
        if (overlay) overlay.remove();
        if (portal) portal.remove();

        // Minor delay allows Joyride to unmount its internal states safely
        setTimeout(() => setIsTourActive(false), 150);
        
        localStorage.setItem(TOUR_STORAGE_KEY, 'true');
        return;
      }

      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        let newStepIndex = index;

        if (type === EVENTS.STEP_AFTER) {
          newStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
        } else if (type === EVENTS.TARGET_NOT_FOUND) {
          // Joyride halts when target is missing. We wait, then resume or skip.
          setRun(false);
          const currentStep = steps[index];
          if (currentStep) {
            waitForTarget(currentStep.target, 3000).then((found) => {
              if (found) {
                setRun(true);
              } else {
                setStepIndex(index + 1);
                setTimeout(() => setRun(true), 100);
              }
            });
          }
          return;
        }

        const nextStep = steps[newStepIndex];
        if (!nextStep) return;

        // Routing trigger checking
        if (nextStep.route && nextStep.route !== location.pathname) {
          setRun(false); // Pause execution
          navigate(nextStep.route);

          waitForTarget(nextStep.target, 4000).then((found) => {
            setStepIndex(newStepIndex);
            if (found) {
              setTimeout(() => setRun(true), 150);
            } else {
              // Proceed anyway if not found (Joyride will throw TARGET_NOT_FOUND and we will skip it)
              setTimeout(() => setRun(true), 150);
            }
          });
        } else {
          setStepIndex(newStepIndex);
        }
      }
    },
    [steps, location.pathname, navigate]
  );


  const contextValue = useMemo(
    () => ({
      startTour,
      stopTour,
      resetTour,
      isTourActive,
      stepIndex,
      totalSteps: steps.length,
      hasCheckedFlag,
      run,
    }),
    [startTour, stopTour, resetTour, isTourActive, stepIndex, steps.length, hasCheckedFlag, run]
  );

  console.log("[Tour] render - run:", run, "step:", stepIndex, "steps len:", steps.length);

  return (
    <TourContext.Provider value={contextValue}>
      {children}
      {Joyride && isTourActive ? (
        <Joyride
          steps={steps}
          stepIndex={stepIndex}
          run={run}
          continuous
          showSkipButton
          showProgress={false}
          dismissKeyAction={false}
          overlayClickAction={false}
          styles={{
            options: {
              zIndex: 10000,
              arrowColor: '#ffffff',
              overlayColor: 'rgba(15, 23, 42, 0.45)',
              spotlightRadius: 16,
            }
          }}
          floatingOptions={{
            onPosition: undefined
          }}
          scrollOffset={100}
          tooltipComponent={TourTooltip}
          onEvent={handleJoyrideCallback}
          spotlightClicks={true}
          locale={{
            back: 'Back',
            close: 'Close',
            last: 'Finish',
            next: 'Next',
            skip: 'Skip tour',
          }}
        />
      ) : null}
    </TourContext.Provider>
  );
}
